/**
 * PaymentPageV2 - Main entry point for the new payment page
 *
 * Features:
 * - Wraps with Stripe Elements provider
 * - Two-column responsive layout (desktop)
 * - Single column with correct ordering (mobile)
 * - Sticky CTA button on mobile only
 */

import { Elements } from '@stripe/react-stripe-js';
import { useSearchParams } from 'react-router-dom';
import usePaymentController, { useStripeLoadStatus } from '@/app/components/payment/payment.controller';
import { StripeLoadError } from '@/app/components/payment/StripeLoadError';
import useCollectPaymentMethod from '@/app/components/payment/components/method/payment-method-v2.controller';
import useOnMount from '@/hooks/useOnMount';
import { useSetIntercomIsVisible } from '@/queries/layout.queries';
import {
  StripeAmountProvider,
  useStripeAmount,
} from '@/app/components/ppra/components/ppra-payment-page/contexts/StripeAmountContext';
import { useStripeAmountUpdater } from '@/app/components/ppra/components/ppra-payment-page/hooks/useStripeAmountUpdater';

import { PaymentPageLayout } from './PaymentPageLayout';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { FeaturesList } from './components/left-column/FeaturesList';
import { TestimonialsSection } from './components/left-column/TestimonialsSection';
import { PrivacySection } from './components/left-column/PrivacySection';
import { FAQSection } from './components/FAQSection';
import { PaymentCard } from './components/right-column/PaymentCard';
import { FooterLinks } from './components/FooterLinks';
import { FooterDisclaimer } from './components/FooterDisclaimer';
import { ContactButton } from './components/ContactButton';
import { StickyMobileCTA } from './components/StickyMobileCTA';
import { FEATURE_ITEMS, MULTI_DISEASE_FEATURE_ITEMS } from './constants';

import {
  PricingHeader,
  EmailField,
  NameField,
  CardSection,
  ExpressCheckoutSection,
  ZipCodeField,
  CouponInput,
  SubmitButton,
  TrialBenefits,
  TrialChargeNotice,
  PaymentTrustBadges,
  LegalDisclaimer,
  HsaFsaBadge,
  HsaFsaDisclaimer,
} from '@/components/payment-form';
import useVisitorSession from '@/hooks/useVisitorSession';
import authService from '@/services/auth.service';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { useCallback, useEffect, useRef, useState } from 'react';
import { COHORT_STORAGE_KEY } from '@/constants/payment';

/**
 * Get cohort from URL param or sessionStorage (for redirect preservation).
 * URL param takes precedence; if found, it's stored for later retrieval.
 * Defaults to 'cancer' if no cohort is specified.
 */
function getCohort(searchParams: URLSearchParams): string {
  const urlCohort = searchParams.get('cohort');

  if (urlCohort) {
    // Store in sessionStorage for redirect preservation
    sessionStorage.setItem(COHORT_STORAGE_KEY, urlCohort);
    return urlCohort;
  }

  // Check sessionStorage (after 3DS redirect)
  const storedCohort = sessionStorage.getItem(COHORT_STORAGE_KEY);
  if (storedCohort) {
    return storedCohort;
  }

  // Default to cancer cohort and persist for redirect preservation
  sessionStorage.setItem(COHORT_STORAGE_KEY, 'cancer');
  return 'cancer';
}

/**
 * PaymentForm - The actual payment form with all fields
 */
function PaymentForm() {
  useStripeAmountUpdater();
  const [searchParams] = useSearchParams();

  // Detect multi-disease cohort from URL params or sessionStorage
  const cohort = getCohort(searchParams);
  const isMultiDiseaseCohort = cohort === 'multi-disease';
  const featureItems = isMultiDiseaseCohort ? MULTI_DISEASE_FEATURE_ITEMS : FEATURE_ITEMS;

  // Set Amplitude user property when multi-disease cohort is detected
  const { setMultiVerticalExperimentProperty } = useAnalyticsCalls();
  const hasSetCohortRef = useRef(false);
  useEffect(() => {
    if (isMultiDiseaseCohort && !hasSetCohortRef.current) {
      hasSetCohortRef.current = true;
      setMultiVerticalExperimentProperty();
    }
  }, [isMultiDiseaseCohort, setMultiVerticalExperimentProperty]);

  const {
    coupon,
    setCoupon,
    couponValidationError,
    isSubmitting,
    getPrice,
    handleSubmit,
    handleExpressCheckout,
    isTrial,
    isPriceLoading,
    priceRecurring,
    priceSubtotal,
    email: sessionEmail,
    form,
  } = useCollectPaymentMethod();

  const { captureLeadAndName } = useVisitorSession();
  const hasLeadCapturedRef = useRef(false);

  // Prefill name from local storage on mount
  useOnMount(() => {
    const firstName = authService.getVisitorFirstName();
    if (firstName) {
      form.setValue('name', firstName);
    }
  });

  // Capture lead when email field is blurred with valid email
  const handleLeadCapture = useCallback(
    async (email: string) => {
      if (hasLeadCapturedRef.current) return;
      hasLeadCapturedRef.current = true;

      const fullName = form.getValues('name');
      try {
        await captureLeadAndName({
          email,
          name: fullName || undefined, // Full name → UserProfile.name in DB
        });
      } catch (error) {
        // Reset flag so they can try again
        hasLeadCapturedRef.current = false;
        console.error('Failed to capture lead:', error);
      }
    },
    [captureLeadAndName, form]
  );

  // Capture name when name field is blurred (updates lead if already captured)
  const handleNameCapture = useCallback(
    async (fullName: string) => {
      // If lead already captured, send name update to backend
      if (hasLeadCapturedRef.current) {
        const email = form.getValues('email');
        if (email) {
          try {
            await captureLeadAndName({
              email,
              name: fullName, // Full name → UserProfile.name in DB
            });
          } catch (error) {
            console.error('Failed to update name:', error);
          }
        }
      }
      // If lead not yet captured, name will be included when email is captured
    },
    [captureLeadAndName, form]
  );

  // Regular annual price (without discount) - derived from API subtotal
  const regularAnnualPrice =
    typeof priceSubtotal === 'number' ? Math.round(priceSubtotal / 100) : undefined;

  // Discounted first charge (when coupon applied) - from recurring.amount
  const discountedFirstCharge =
    typeof priceRecurring?.amount === 'number'
      ? Math.round(priceRecurring.amount / 100)
      : undefined;

  // Use discounted price for PricingHeader display, fallback to regular
  const annualPrice = discountedFirstCharge ?? regularAnnualPrice;

  // Check if form is valid for sticky CTA
  const isFormValid = form.formState.isValid;

  // Track visibility of main submit button to show/hide sticky CTA
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const [isSubmitButtonVisible, setIsSubmitButtonVisible] = useState(true);

  useEffect(() => {
    const checkSubmitButtonVisibility = () => {
      if (submitButtonRef.current) {
        const rect = submitButtonRef.current.getBoundingClientRect();
        // Button is visible if it's within the viewport
        // (top is above viewport bottom AND bottom is below viewport top)
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        setIsSubmitButtonVisible(isInViewport);
      }
    };

    window.addEventListener('scroll', checkSubmitButtonVisibility);
    window.addEventListener('resize', checkSubmitButtonVisibility);
    checkSubmitButtonVisibility();

    return () => {
      window.removeEventListener('scroll', checkSubmitButtonVisibility);
      window.removeEventListener('resize', checkSubmitButtonVisibility);
    };
  }, []);

  // Handle sticky CTA click
  // If form is valid, submit payment; otherwise scroll to payment card
  const handleStickyCTAClick = () => {
    if (isFormValid) {
      handleSubmit();
    } else {
      const paymentCard = document.getElementById('payment-card');
      if (paymentCard) {
        paymentCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <PaymentPageLayout
      header={<Header />}
      hero={<HeroSection showSubhead={false} isMultiDiseaseCohort={isMultiDiseaseCohort} />}
      paymentCard={
        <PaymentCard id="payment-card" isSticky={false}>
          {/* Pricing */}
          <PricingHeader
            isTrial={isTrial}
            annualPrice={annualPrice}
            isLoading={isPriceLoading}
          />

          <HsaFsaBadge />

          {/* Express Checkout - wallets first as primary option */}
          <ExpressCheckoutSection onConfirm={handleExpressCheckout} />

          {/* Form Fields - manual card entry */}
          <form onSubmit={form.handleSubmit(() => handleSubmit())}>
            <div className="flex flex-col gap-4">
              <EmailField
                registration={form.register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={form.formState.errors.email?.message}
                onLeadCapture={handleLeadCapture}
              />

              <NameField
                registration={form.register('name', {
                  required: 'Name is required',
                })}
                error={form.formState.errors.name?.message}
                onNameCapture={handleNameCapture}
              />

              <CardSection
                email={form.watch('email')}
                name={form.watch('name')}
              />

              <ZipCodeField
                registration={form.register('zip', {
                  required: 'Zip code is required',
                  pattern: {
                    value: /^\d{5}$/,
                    message: 'Enter a valid 5-digit zip code',
                  },
                })}
                error={form.formState.errors.zip?.message}
              />

              <CouponInput
                value={coupon}
                onChange={setCoupon}
                onValidate={() => getPrice()}
                error={couponValidationError || undefined}
              />
            </div>

            {/* Submit */}
            <div ref={submitButtonRef} className="mt-6 flex flex-col gap-3">
              <SubmitButton isTrial={isTrial} isSubmitting={isSubmitting} />
              {isTrial && (
                <TrialChargeNotice
                  annualPrice={regularAnnualPrice}
                  discountedFirstCharge={discountedFirstCharge}
                />
              )}
              <LegalDisclaimer />
            </div>
          </form>

          {/* Benefits & Trust */}
          <TrialBenefits isTrial={isTrial} />
          <PaymentTrustBadges />
          <HsaFsaDisclaimer />
        </PaymentCard>
      }
      featuresList={<FeaturesList items={featureItems} />}
      testimonials={<TestimonialsSection />}
      privacy={<PrivacySection />}
      faqs={
        <div id="faqs">
          <FAQSection faqSetSlug={isMultiDiseaseCohort ? 'payment-multi-disease' : 'payment-cancer'} />
        </div>
      }
      contactButton={<ContactButton />}
      footer={
        <div className="flex flex-col gap-6">
          <FooterLinks />
          <FooterDisclaimer />
        </div>
      }
      stickyMobileCTA={
        <StickyMobileCTA
          isEnabled={isFormValid}
          onClick={handleStickyCTAClick}
          isSubmitting={isSubmitting}
          isVisible={!isSubmitButtonVisible}
        >
          Start my risk assessment
        </StickyMobileCTA>
      }
    />
  );
}

/**
 * Wrapper that provides Stripe Elements context
 */
function PaymentPageWithElements({ children }: { children: React.ReactNode }) {
  const { amount } = useStripeAmount();
  const [searchParams] = useSearchParams();
  const isTrial = searchParams.get('trial') !== 'false';

  const { stripePromise, stripeOptions } = usePaymentController({
    variant: 'seamless',
    amount: amount ?? 0,
    isTrial,
  });

  const stripeStatus = useStripeLoadStatus(stripePromise);

  if (stripeStatus === 'failed') {
    return <StripeLoadError />;
  }

  return (
    <Elements options={stripeOptions} stripe={stripePromise}>
      {children}
    </Elements>
  );
}

/**
 * Main exported component
 */
export function PaymentPageV2() {
  const setIntercomIsVisible = useSetIntercomIsVisible();

  useOnMount(() => {
    window.scrollTo(0, 0);
    setIntercomIsVisible.mutate(false);
    return () => {
      setIntercomIsVisible.mutate(true);
    };
  });

  return (
    <StripeAmountProvider>
      <PaymentPageWithElements>
        <PaymentForm />
      </PaymentPageWithElements>
    </StripeAmountProvider>
  );
}

export default PaymentPageV2;
